import { useState } from 'react';
import type { Meal } from '@meal-planner/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Icon } from './ui/Icon.js';

interface FavoriteRecipe { id: string; recipeId: string; recipeName: string; mealType: string; createdAt: string; }

interface Props {
  meal: Meal;
  isLocked: boolean;
  onToggleLock: () => void;
  onClick: () => void;
  showPickyTag: boolean;
  // G2 Area 3 — true when this meal's recipeId appears in plan.swapLog
  isBudgetSwap?: boolean;
  // G2 Areas 4–5 — mark-as-cooked
  planId?: string;
  mealDate?: string;
  isCooked?: boolean;
}

const MEAL_TYPE_ACCENT: Record<string, string> = {
  breakfast: 'accent-saffron',
  lunch: 'accent-cobalt',
  dinner: 'accent-plum',
};

const MEAL_TYPE_LABEL: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Easy',
  intermediate: 'Medium',
  expert: 'Hard',
};

const DIFFICULTY_DOTS: Record<string, number> = {
  easy: 1,
  intermediate: 2,
  expert: 3,
};

const METHOD_LABEL: Record<string, string> = {
  stovetop:    'Stovetop',
  oven:        'Oven',
  air_fryer:   'Air fryer',
  instant_pot: 'Instant Pot',
  slow_cooker: 'Slow cooker',
  microwave:   'Microwave',
  grill:       'Grill',
};

export function MealChip({ meal, isLocked, onToggleLock, onClick, showPickyTag, isBudgetSwap = false, planId, mealDate, isCooked = false }: Props) {
  const [optimisticCooked, setOptimisticCooked] = useState(false);
  const accentClass = MEAL_TYPE_ACCENT[meal.type] ?? 'accent-emerald';
  const diffLabel = meal.difficulty ? DIFFICULTY_LABEL[meal.difficulty] : null;
  const diffDots = meal.difficulty ? DIFFICULTY_DOTS[meal.difficulty] ?? 1 : 0;
  const primaryMethod = meal.cookingMethod?.[0];
  const methodLabel = primaryMethod ? METHOD_LABEL[primaryMethod] : null;

  const qc = useQueryClient();
  const { data: favorites = [] } = useQuery<FavoriteRecipe[]>({
    queryKey: ['favorites'],
    queryFn: () => api.get('/api/favorites'),
    staleTime: 5 * 60_000,
  });
  const isFav = !!meal.recipeId && favorites.some((f) => f.recipeId === meal.recipeId);
  const toggleFav = useMutation<unknown, Error, void, { prev: FavoriteRecipe[] | undefined }>({
    mutationFn: async () => {
      if (!meal.recipeId) return;
      return isFav
        ? api.delete(`/api/favorites/${meal.recipeId}`)
        : api.post('/api/favorites', { recipeId: meal.recipeId, recipeName: meal.name, mealType: meal.type });
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['favorites'] });
      const prev = qc.getQueryData<FavoriteRecipe[]>(['favorites']);
      if (meal.recipeId) {
        qc.setQueryData<FavoriteRecipe[]>(['favorites'], (old = []) =>
          isFav
            ? old.filter((f) => f.recipeId !== meal.recipeId)
            : [...old, { id: `optimistic-${meal.recipeId}`, recipeId: meal.recipeId!, recipeName: meal.name, mealType: meal.type, createdAt: new Date().toISOString() }]
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['favorites'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const markCooked = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      if (!planId || !mealDate) return;
      return api.patch(`/api/meal-plans/${planId}/mark-cooked`, {
        date: mealDate,
        mealType: meal.type,
      });
    },
    onMutate: () => {
      setOptimisticCooked(true);
    },
    onError: () => {
      setOptimisticCooked(false);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-plan', planId] });
    },
  });

  return (
    <div
      className={`relative rounded-lg transition-all ${accentClass}`}
      style={{
        background: isLocked ? 'var(--accent-soft)' : 'var(--surface)',
        border: `1px solid ${isLocked ? 'transparent' : 'var(--line-soft)'}`,
      }}
    >
      <button
        onClick={onClick}
        className="w-full text-left p-2 pr-28 rounded-lg focus:outline-none focus-visible:ring-2 min-h-[44px] transition-opacity"
        style={{
          '--tw-ring-color': 'var(--accent)',
          opacity: optimisticCooked || isCooked ? 0.6 : 1,
        } as React.CSSProperties}
        aria-label={`View recipe for ${meal.name}`}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span
            className="inline-block w-1 h-1 rounded-full flex-shrink-0"
            style={{ background: 'var(--accent)' }}
          />
          <span
            className="text-[10px] font-semibold tracking-[0.1em] uppercase"
            style={{ color: 'var(--accent)' }}
          >
            {MEAL_TYPE_LABEL[meal.type] ?? meal.type}
          </span>
          {isBudgetSwap && (
            <span
              className="text-[10px] font-semibold tracking-[0.05em] uppercase px-1.5 py-0.5 rounded leading-none"
              style={{
                color: 'oklch(0.55 0.14 75)',
                background: 'oklch(0.94 0.06 80)',
              }}
              title="Swapped to fit your weekly budget"
            >
              ↓ budget
            </span>
          )}
        </div>

        <p
          className="text-xs font-medium leading-snug line-clamp-2 mb-1.5"
          style={{
            color: 'var(--ink)',
            textDecoration: optimisticCooked || isCooked ? 'line-through' : 'none',
          }}
        >
          {meal.name}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          {diffLabel && (
            <span
              className="inline-flex items-center gap-1 text-[10px]"
              style={{ color: 'var(--ink-3)' }}
              title={`${diffLabel} difficulty`}
            >
              <span className="inline-flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: i < diffDots ? 'var(--accent)' : 'var(--line-soft)',
                    }}
                  />
                ))}
              </span>
              {diffLabel}
            </span>
          )}
          {meal.cookTimeMins !== undefined && (
            <span
              className="inline-flex items-center gap-1 text-[10px]"
              style={{ color: 'var(--ink-3)' }}
              title={`${meal.cookTimeMins} minutes total`}
            >
              <Icon name="clock" size={10} />
              {meal.cookTimeMins}m
            </span>
          )}
          {methodLabel && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded leading-tight"
              style={{
                color: 'var(--ink-2)',
                background: 'color-mix(in oklab, var(--accent) 14%, var(--surface))',
              }}
              title={meal.cookingMethod && meal.cookingMethod.length > 1
                ? `Uses ${meal.cookingMethod.map((m) => METHOD_LABEL[m] ?? m).join(', ')}`
                : `Uses ${methodLabel.toLowerCase()}`}
            >
              {methodLabel}
            </span>
          )}
          {showPickyTag && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded"
              style={{
                color: 'var(--accent)',
                background: 'var(--accent-soft)',
              }}
            >
              Picky-friendly
            </span>
          )}
        </div>

        {meal.sides && meal.sides.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {meal.sides.map((side, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded-sm leading-tight max-w-full truncate"
                style={{
                  color: 'var(--ink-3)',
                  background: 'var(--surface-2)',
                }}
              >
                + {side.name}
              </span>
            ))}
          </div>
        )}
      </button>

      {!isLocked && planId && mealDate && !isCooked && !optimisticCooked && (
        <button
          onClick={(e) => { e.stopPropagation(); markCooked.mutate(); }}
          disabled={markCooked.isPending}
          aria-label={`Mark ${meal.name} as cooked`}
          title="Mark as cooked"
          className="absolute top-1.5 p-1 rounded transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center"
          style={{
            right: meal.recipeId ? '52px' : '36px',
            color: 'var(--ink-4)',
            opacity: markCooked.isPending ? 0.55 : 1,
          }}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      )}

      {meal.recipeId && (
        <button
          onClick={(e) => { e.stopPropagation(); toggleFav.mutate(); }}
          disabled={toggleFav.isPending}
          aria-label={isFav ? `Remove ${meal.name} from favorites` : `Save ${meal.name} to favorites`}
          title={isFav ? 'Saved — click to remove' : 'Save to favorites'}
          aria-pressed={isFav}
          className="absolute top-1.5 p-1 rounded transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center"
          style={{
            right: '36px',
            color: isFav ? 'var(--danger)' : 'var(--ink-4)',
            opacity: toggleFav.isPending ? 0.55 : 1,
          }}
        >
          <Icon name="heart" size={14} stroke={isFav ? 2.4 : 1.8} />
        </button>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onToggleLock(); }}
        aria-label={isLocked ? `Unlock ${meal.name}` : `Lock ${meal.name}`}
        title={isLocked ? 'Locked — click to unlock' : 'Click to lock this meal'}
        className="absolute top-1.5 right-1.5 p-1 rounded transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center"
        style={{
          color: isLocked ? 'var(--accent)' : 'var(--ink-4)',
        }}
      >
        {isLocked ? (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17 8V7a5 5 0 0 0-10 0v1H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2zm-5 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3-7H9V7a3 3 0 0 1 6 0v1z"/>
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
          </svg>
        )}
      </button>
    </div>
  );
}
